import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Space, Spin } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

type ErrorType =
  | 'invalid'
  | 'expired'
  | 'revoked'
  | 'accepted'
  | 'admin_exists'
  | null;

interface AcceptFormValues {
  name?: string;
  password: string;
  confirmPassword: string;
}

const ERROR_MESSAGES: Record<
  NonNullable<ErrorType>,
  { title: string; desc: string }
> = {
  invalid: {
    title: 'Liên kết không hợp lệ',
    desc: 'Liên kết mời không hợp lệ hoặc đã hết hạn. Vui lòng liên hệ Super Admin để được mời lại.',
  },
  expired: {
    title: 'Lời mời đã hết hạn',
    desc: 'Lời mời đã hết hạn. Vui lòng liên hệ Super Admin để gửi lại.',
  },
  revoked: {
    title: 'Lời mời đã bị hủy',
    desc: 'Lời mời đã bị hủy.',
  },
  accepted: {
    title: 'Lời mời đã được sử dụng',
    desc: 'Lời mời đã được sử dụng. Vui lòng đăng nhập.',
  },
  admin_exists: {
    title: 'Admin đã tồn tại',
    desc: 'Email này đã là Admin. Vui lòng đăng nhập hoặc liên hệ Super Admin.',
  },
};

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!token);
  const [errorType, setErrorType] = useState<ErrorType>(null);

  useEffect(() => {
    if (!token) {
      setErrorType('invalid');
      setVerifying(false);
      return;
    }
    setVerifying(false);
  }, [token]);

  const onFinish = async (values: AcceptFormValues) => {
    if (!token) return;
    if (values.password !== values.confirmPassword) return;

    setLoading(true);
    setErrorType(null);

    try {
      const res = await api.post('/public/admin-invitations/accept', {
        token,
        password: values.password,
        name: values.name?.trim() || undefined,
      });

      const data = res.data;
      const jwt = data.token;
      if (data.success && jwt) {
        const ok = await loginWithToken(jwt);
        if (ok) {
          navigate('/', { replace: true });
          return;
        }
      }
      navigate('/login', {
        state: { message: 'Đã chấp nhận lời mời! Bạn có thể đăng nhập.' },
        replace: true,
      });
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          data?: { message?: string };
          status?: number;
        };
      };
      const msg = (ax.response?.data?.message || '').toLowerCase();
      const status = ax.response?.status;

      if (
        status === 409 ||
        msg.includes('admin đã tồn tại') ||
        msg.includes('already')
      ) {
        setErrorType('admin_exists');
      } else if (
        msg.includes('hết hạn') ||
        msg.includes('expired') ||
        msg.includes('invalid') ||
        msg.includes('liên kết không hợp lệ')
      ) {
        setErrorType(msg.includes('hết hạn') ? 'expired' : 'invalid');
      } else if (msg.includes('hủy') || msg.includes('revoked')) {
        setErrorType('revoked');
      } else if (
        msg.includes('đã được sử dụng') ||
        msg.includes('already accepted')
      ) {
        setErrorType('accepted');
      } else {
        setErrorType('invalid');
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 20,
        }}
      >
        <Card
          style={{
            width: 400,
            textAlign: 'center',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>
            Đang xác minh lời mời...
          </div>
        </Card>
      </div>
    );
  }

  if (!token || errorType) {
    const config = ERROR_MESSAGES[errorType || 'invalid'];

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 20,
        }}
      >
        <Card
          style={{
            width: 440,
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          }}
        >
          <Title level={3} style={{ marginBottom: 16 }}>
            {config.title}
          </Title>
          <Text type="secondary">{config.desc}</Text>
          <div style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" onClick={() => navigate('/login')}>
                Đăng nhập
              </Button>
              <Text type="secondary">Liên hệ Super Admin nếu cần hỗ trợ.</Text>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 20,
      }}
    >
      <Card
        style={{
          width: 400,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 12,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
              Chấp nhận lời mời Admin
            </Title>
            <Text type="secondary">
              Hoàn tất thiết lập tài khoản bằng cách tạo mật khẩu.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Lời mời có hiệu lực 3 ngày.
              </Text>
            </div>
          </div>

          <Form
            name="accept-invitation"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item name="name" label="Tên (tùy chọn)">
              <Input
                prefix={<UserOutlined />}
                placeholder="Tên hiển thị của bạn"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Mật khẩu"
              rules={[
                { required: true, message: 'Vui lòng nhập mật khẩu' },
                { min: 8, message: 'Mật khẩu phải có ít nhất 8 ký tự' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Xác nhận mật khẩu"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Vui lòng xác nhận mật khẩu' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Mật khẩu không khớp'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                style={{ width: '100%' }}
              >
                Chấp nhận lời mời
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Cần hỗ trợ? Liên hệ Super Admin.</Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}
